import {useContext, useEffect, useState} from "react";
import {Context as AppContext} from "../../../context/AppContext";
import umAxios from "../../../config/axios";
import {toast} from "react-toastify";
import {CategoryRepository} from "../../../repositories/CategoryRepository";
import {SignInContext} from "../../../context/SignInContext";
import {
  Box,
  Button, Pagination,
  Stack,
  Table,
  TableBody,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {grey} from "@mui/material/colors";
import {Link} from "react-router-dom";
import {IconPlus} from "@tabler/icons-react";
import ProjectRichTableRow from "./project-rich-table-row";

const rowsPerPage = 5;


const filterPillSx = (isSelected) => ({
  bgcolor: isSelected ? grey[200] : "transparent",
  color: isSelected ? grey[900] : grey[600],
  fontWeight: isSelected ? 600 : 500,
  borderRadius: "20px",
  py: 0.5, px: 2, minWidth: 0,
  textWrap: "nowrap",
  transition: "background-color 0.15s ease",
  "&:hover": {
    bgcolor: isSelected ? grey[300] : "action.hover",
  },
});

export const ProjectRichTableView = () => {
  const {currentUser, isAuth, organizations} = useContext(SignInContext);
  const {state: {projects}} = useContext(AppContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // Pagination state
  const [page, setPage] = useState(0);
  const [languages, setLanguages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await umAxios.get('/api/language-support/all');
        setLanguages(response.data);
      } catch (err) {
        toast.error(err.message);
      }
    };

    if (isAuth) {
      fetchLanguages();
      fetchCategories();
    }
  }, [isAuth, organizations]);

  const fetchCategories = async () => {
    const isAdmin = currentUser?.roles?.includes("admin");
    const fetcher = isAdmin ? CategoryRepository.getAll : () => CategoryRepository.getCategoriesByMember(currentUser.username);
    fetcher()
        .then(response => setCategories(response.data))
        .catch(error => console.log(error));
  };

  const sortedProjects = [...(projects || [])]
      .filter(i => selectedOrganization === null || i?.settings?.organization === selectedOrganization?.id)
      .sort((a, b) => new Date(b?.updatedAt || b?.createdAt) - new Date(a?.updatedAt || a?.createdAt));

  const favoriteProjects = sortedProjects.filter(project =>
      project?.settings?.favourites?.includes(currentUser?.username)
  );

  const otherProjects = sortedProjects.filter(project =>
      !project?.settings?.favourites?.includes(currentUser?.username)
  );

  const displayProjects = [...favoriteProjects, ...otherProjects];


  return (
      <Stack direction={"column"} gap={2} maxWidth={"lg"} sx={{mx: "auto", width: {xs: "92%", md: "90%"}, py: 3}}>

        <Stack direction={{xs: "column", md: "row"}} gap={2} justifyContent={"space-between"} alignItems={{xs: "stretch", md: "flex-end"}}>
          <Box>
            <Typography variant={"h4"} fontSize={{xs: "1.25em", md: "1.5em"}} fontWeight={600} gutterBottom>Projects</Typography>
            <Stack direction={"row"} gap={1} alignItems={"center"} flexWrap={"wrap"}>
              <Typography
                  color={grey[500]}
                  fontWeight={600}
                  fontSize={"0.72em"}
                  letterSpacing={"0.06em"}
                  sx={{textTransform: "uppercase", mr: 0.5}}
              >
                Filter by
              </Typography>
              <Button
                  onClick={() => setSelectedOrganization(null)} size={"small"}
                  color={"secondary"}
                  sx={filterPillSx(selectedOrganization === null)}
              >
                All
              </Button>
              {organizations?.map(organization => {
                const isSelected = organization?.id === selectedOrganization?.id;
                return (
                    <Button
                        key={organization?.id}
                        onClick={() => {
                          setSelectedOrganization(organization);
                          setPage(0);
                        }} size={"small"}
                        color={"secondary"}
                        sx={filterPillSx(isSelected)}
                    >
                      {organization?.name}
                    </Button>
                )
              })}
            </Stack>
          </Box>
          <Box>
            <Button
                component={Link} to={"/project/create"}
                sx={{textWrap: "nowrap"}} variant={"primary"}
                startIcon={<IconPlus size={18}/>}
            >
              Create Project
            </Button>
          </Box>
        </Stack>

        {projects?.length > 0 && displayProjects?.length === 0 && <Typography variant={"h4"} sx={{py: 3}}>There is not projects for this organization</Typography>}


        {isMobile ? (
            <Stack direction={"column"} gap={2} sx={{mt: 1}}>
              {displayProjects
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map(project => (
                      <ProjectRichTableRow
                          key={project?.id}
                          mobile
                          project={project}
                          organizations={organizations}
                          categories={categories}
                          languages={languages}
                      />
                  ))
              }
            </Stack>
        ) : (
            <Table border={0} sx={{borderSpacing: '0 16px', borderCollapse: 'separate'}}>
              <TableBody>
                {displayProjects
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(project => (
                        <ProjectRichTableRow
                            key={project?.id}
                            project={project}
                            organizations={organizations}
                            categories={categories}
                            languages={languages}
                        />
                    ))
                }
              </TableBody>
            </Table>
        )}

        {displayProjects?.length > 0 ? <Stack direction="row" justifyContent="center" mt={2}>
              <Pagination
                  count={Math.ceil(displayProjects.length / rowsPerPage)}
                  page={page + 1}
                  onChange={(event, value) => setPage(value - 1)}
                  color="primary"
                  shape="rounded"
                  siblingCount={1}
                  boundaryCount={1}
                  showFirstButton
                  showLastButton
              />
            </Stack>
            : null
        }
      </Stack>
  );
}